import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, CalendarDays, Clock, ShoppingCart, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../context/CartContext';
import { API } from '../config/api';
import { COMPANY_INFO } from '../data/siteData';
import { Toaster, toast } from 'sonner';

function CalendarWidget({ selectedDate, onSelect }) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [availability, setAvailability] = useState({});

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const monthName = currentMonth.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  // Fetch availability for visible dates
  useEffect(() => {
    const fetchAvailability = async () => {
      const dates = [];
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        if (date >= today && date.getDay() !== 0) {
          dates.push(`${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
        }
      }
      const results = {};
      // Batch check (max 15 at a time to avoid overload)
      for (let i = 0; i < dates.length; i += 15) {
        const batch = dates.slice(i, i + 15);
        const promises = batch.map((date) =>
          axios.get(`${API}/bookings/availability`, { params: { date } })
            .then((res) => { results[date] = res.data.available_slots; })
            .catch(() => { results[date] = []; })
        );
        await Promise.all(promises);
      }
      setAvailability(results);
    };
    fetchAvailability();
  }, [year, month, daysInMonth]);

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const canGoPrev = new Date(year, month, 1) > today;

  return (
    <div data-testid="booking-calendar">
      {/* Month nav */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} disabled={!canGoPrev} className="p-1.5 rounded-lg hover:bg-sky-50 disabled:opacity-30">
          <ChevronLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h3 className="font-heading font-semibold text-slate-900">{monthName}</h3>
        <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-sky-50">
          <ChevronRight className="w-5 h-5 text-slate-600" />
        </button>
      </div>
      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="text-center text-xs font-medium text-slate-400 py-1">{d}</div>
        ))}
      </div>
      {/* Days */}
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: firstDay }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const date = new Date(year, month, day);
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isPast = date < today;
          const isSunday = date.getDay() === 0;
          const isDisabled = isPast || isSunday;
          const isSelected = selectedDate === dateStr;
          const slots = availability[dateStr];
          const hasSlots = slots && slots.length > 0;
          const isFullyBooked = slots && slots.length === 0;

          return (
            <button
              key={day}
              onClick={() => !isDisabled && hasSlots && onSelect(dateStr)}
              disabled={isDisabled || isFullyBooked}
              className={`aspect-square rounded-lg text-sm font-medium transition-all
                ${isSelected ? 'bg-sky-700 text-white ring-2 ring-sky-300' : ''}
                ${!isSelected && hasSlots ? 'bg-sky-50 text-slate-900 hover:bg-sky-100 cursor-pointer' : ''}
                ${isFullyBooked && !isDisabled ? 'bg-red-50 text-red-300 cursor-not-allowed' : ''}
                ${isDisabled ? 'text-slate-200 cursor-not-allowed' : ''}
                ${isSunday && !isPast ? 'bg-slate-50 text-slate-300' : ''}
              `}
            >
              {day}
            </button>
          );
        })}
      </div>
      <div className="flex gap-4 mt-3 text-xs text-slate-400">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-sky-50 border border-sky-200" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-200" /> Fully booked</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-slate-50 border border-slate-200" /> Closed</span>
      </div>
    </div>
  );
}

export default function Book() {
  const { items, removeItem, clearCart, total } = useCart();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);

  // Fetch slots when date selected
  useEffect(() => {
    if (!selectedDate) return;
    setSelectedSlot(null);
    axios.get(`${API}/bookings/availability`, { params: { date: selectedDate } })
      .then((res) => setAvailableSlots(res.data.available_slots))
      .catch(() => setAvailableSlots([]));
  }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error('Add services to your cart first.'); return; }
    if (!selectedDate) { toast.error('Please select a date.'); return; }
    if (!selectedSlot) { toast.error('Please select a time slot.'); return; }
    if (!form.name || !form.email || !form.address) { toast.error('Please fill in your name, email, and address.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/bookings`, {
        customer_name: form.name,
        email: form.email,
        phone: form.phone || null,
        address: form.address,
        items: items.map((i) => ({ service: i.service, option: i.option, price: i.price })),
        date: selectedDate,
        slot: selectedSlot,
        notes: form.notes || null,
        origin_url: window.location.origin,
      });
      setBooked(res.data);
      clearCart();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (booked) {
    const slotLabel = booked.slot === 'AM' ? '8:00 AM – 11:30 AM' : '12:30 PM – 6:00 PM';
    return (
      <div data-testid="booking-success" className="min-h-[60vh] flex items-center justify-center py-16">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Booking Confirmed!</h2>
          <p className="text-slate-500 text-sm mb-6">
            Your reservation <strong>{booked.booking_number}</strong> is confirmed for{' '}
            <strong>{new Date(booked.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</strong>{' '}
            ({slotLabel}). A confirmation email has been sent to <strong>{booked.email}</strong>.
          </p>
          <p className="text-slate-400 text-xs mb-6">
            Need to reschedule? Call us at <strong>{COMPANY_INFO.phone}</strong>.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={`/book/edit/${booked.id}`} data-testid="edit-booking-link" className="bg-sky-700 text-white font-semibold rounded-full px-6 py-3 hover:bg-sky-600 transition-colors inline-block text-center text-sm">
              Edit Booking or Add Services
            </Link>
            <Link to="/" data-testid="booking-back-home" className="border border-sky-200 text-slate-600 font-semibold rounded-full px-6 py-3 hover:bg-sky-50 transition-colors inline-block text-center text-sm">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="book-page">
      <Toaster position="top-right" richColors />
      {/* Hero */}
      <section className="bg-sky-50 py-14 md:py-18">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 mb-3">Book Your Service</p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-black text-slate-900 mb-3">
            Schedule Your Appointment
          </h1>
          <p className="text-slate-500 text-sm max-w-lg mx-auto">
            Select your services, pick a date and time, and we'll take care of the rest.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <ShoppingCart className="w-16 h-16 text-sky-200 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Your Cart is Empty</h3>
              <p className="text-slate-400 text-sm mb-6">Browse our services and add them to your cart to get started.</p>
              <Link to="/#services" data-testid="browse-services-link" className="bg-sky-700 text-white font-semibold rounded-full px-6 py-3 hover:bg-sky-600 transition-colors inline-block">
                Browse Services
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                {/* Left column: Cart + Info */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Cart */}
                  <div>
                    <h2 className="font-heading text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5 text-sky-700" /> Your Services
                    </h2>
                    <div className="space-y-2" data-testid="cart-items">
                      {items.map((item, i) => (
                        <div key={i} className="flex items-center justify-between bg-white rounded-xl border border-sky-100 px-4 py-3" data-testid={`cart-item-${i}`}>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{item.service}</p>
                            <p className="text-xs text-slate-400">{item.option}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-heading font-bold text-sky-700">${item.price.toFixed(2)}</span>
                            <button type="button" onClick={() => removeItem(i)} data-testid={`remove-cart-item-${i}`} className="p-1 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-3 px-1">
                      <Link to="/" className="text-sky-700 text-sm font-medium hover:underline">+ Add more services</Link>
                      <p className="font-heading text-lg font-bold text-slate-900">Total: <span className="text-sky-700">${total.toFixed(2)}</span></p>
                    </div>
                  </div>

                  {/* Customer info */}
                  <div>
                    <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Your Information</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-slate-700 block mb-1">Full Name *</label>
                        <Input data-testid="book-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Smith" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-700 block mb-1">Email *</label>
                        <Input type="email" data-testid="book-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="john@example.com" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-700 block mb-1">Phone</label>
                        <Input data-testid="book-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(239) 555-0123" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-700 block mb-1">Service Address *</label>
                        <Input data-testid="book-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="123 Main St, Naples, FL" />
                      </div>
                    </div>
                    <div className="mt-4">
                      <label className="text-xs font-medium text-slate-700 block mb-1">Notes (optional)</label>
                      <Textarea data-testid="book-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any special instructions or gate codes..." rows={3} />
                    </div>
                  </div>
                </div>

                {/* Right column: Calendar + Slot */}
                <div className="lg:col-span-1">
                  <div className="sticky top-32 space-y-6">
                    {/* Calendar */}
                    <div className="bg-white rounded-2xl border border-sky-100 shadow-lg p-5">
                      <h2 className="font-heading text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <CalendarDays className="w-5 h-5 text-sky-700" /> Select a Date
                      </h2>
                      <CalendarWidget selectedDate={selectedDate} onSelect={setSelectedDate} />
                    </div>

                    {/* Time slot */}
                    {selectedDate && (
                      <div className="bg-white rounded-2xl border border-sky-100 shadow-lg p-5">
                        <h2 className="font-heading text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
                          <Clock className="w-5 h-5 text-sky-700" /> Select a Time
                        </h2>
                        {availableSlots.length === 0 ? (
                          <p className="text-slate-400 text-sm">No available slots for this date.</p>
                        ) : (
                          <div className="space-y-2" data-testid="time-slots">
                            {availableSlots.map((s) => (
                              <button
                                key={s.slot}
                                type="button"
                                onClick={() => setSelectedSlot(s.slot)}
                                data-testid={`slot-${s.slot}`}
                                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all text-sm font-medium ${
                                  selectedSlot === s.slot
                                    ? 'border-sky-700 bg-sky-50 text-sky-700'
                                    : 'border-sky-100 bg-white text-slate-700 hover:border-sky-300'
                                }`}
                              >
                                {s.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Book button */}
                    <Button
                      type="submit"
                      disabled={submitting || !selectedDate || !selectedSlot}
                      data-testid="book-submit-btn"
                      className="w-full bg-sky-700 text-white font-semibold rounded-full py-3 hover:bg-sky-600 text-base disabled:opacity-50"
                    >
                      {submitting ? 'Booking...' : `Book for $${total.toFixed(2)}`}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
